import React from 'react';
import type { EmergencyState } from '../../receptionist-settings/types';
import { RCPT_SETTINGS_EMERGENCY_RULES } from '../../receptionist-settings/mockData';
import {
  SettingsDirtyBlock,
  SettingsFieldRow,
  SettingsPageHeader,
  SettingsRuleCard,
  SettingsSectionHeader,
  SettingsSelect,
  SettingsToggle,
} from './SettingsShared';

export interface EmergencySettingsPageProps {
  state: EmergencyState;
  setState: React.Dispatch<React.SetStateAction<EmergencyState>>;
  initial: EmergencyState;
}

export function EmergencySettingsPage({ state, setState, initial }: EmergencySettingsPageProps) {
  const upd = (key: keyof EmergencyState, value: EmergencyState[keyof EmergencyState]) =>
    setState((s) => ({ ...s, [key]: value }));
  const dirty = (key: keyof EmergencyState) => state[key] !== initial[key];

  return (
    <>
      <SettingsPageHeader
        eyebrow="Behavior · safety"
        title="Emergency"
        italic="detection"
        description="What counts as urgent at your practice. Elva uses these criteria to triage every incoming call and decides when to bypass standard scheduling, page the on-call provider, or escalate to staff."
      />

      <section className="set-fade-up set-d-1 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="01"
            title="What counts as an emergency at your"
            italic="practice?"
            sub="Describe symptoms, pain levels, time windows, and post-op signals. Elva uses this text to triage every incoming call."
            railColor="var(--set-negative)"
            action={{ label: 'Re-analyze' }}
            actionType="pill"
          />
          <SettingsDirtyBlock isDirty={dirty('definition')} className="px-6 py-5">
            <textarea
              className="set-textarea min-h-[200px] text-[14px] leading-[1.65]"
              value={state.definition}
              onChange={(e) => upd('definition', e.target.value)}
              rows={8}
            />
          </SettingsDirtyBlock>
        </div>
      </section>

      <section className="set-fade-up set-d-2 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="02"
            title="Triage rules Elva extracted"
            sub="Each line above maps to a discrete clinical rule. Review before going live."
            railColor="var(--set-negative)"
          />
          <div className="flex flex-col gap-3 px-6 py-[18px]">
            {RCPT_SETTINGS_EMERGENCY_RULES.map((r, i) => (
              <SettingsRuleCard key={i} {...r} />
            ))}
          </div>
        </div>
      </section>

      <section className="set-fade-up set-d-3 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="03"
            title="How Elva schedules emergencies"
            sub="Direct controls — applied to every call she classifies as urgent."
            railColor="var(--set-negative)"
          />
          <SettingsFieldRow
            title="Allow double-booking emergencies"
            description="When on, Elva can squeeze an emergency into a slot that's already taken — the provider sees both patients back-to-back."
            isDirty={dirty('allowDoubleBooking')}
          >
            <SettingsToggle value={state.allowDoubleBooking} onChange={(v) => upd('allowDoubleBooking', v)} />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Reserved emergency slots per day"
            description="Slots kept open at the start of each day for true emergencies."
            isDirty={dirty('reservedSlotsDaily')}
          >
            <SettingsSelect
              value={String(state.reservedSlotsDaily)}
              onChange={(v) => upd('reservedSlotsDaily', parseInt(v, 10))}
              width={140}
              options={[0, 1, 2, 3, 4].map((n) => ({
                value: String(n),
                label: `${n} slot${n === 1 ? '' : 's'}`,
              }))}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Skip insurance verification"
            description="Skip the eligibility step for true emergencies — verify after the appointment instead."
            isDirty={dirty('skipInsuranceVerification')}
          >
            <SettingsToggle
              value={state.skipInsuranceVerification}
              onChange={(v) => upd('skipInsuranceVerification', v)}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="After-hours emergency routing"
            description="What Elva does when an emergency comes in outside open hours."
            isDirty={dirty('afterHoursRoute')}
            last
          >
            <SettingsSelect
              value={state.afterHoursRoute}
              onChange={(v) => upd('afterHoursRoute', v)}
              width={240}
              options={[
                { value: 'oncall', label: 'Page the on-call dentist' },
                { value: 'er', label: 'Refer to nearest ER' },
                { value: 'callback', label: 'First-thing-morning callback' },
                { value: 'voicemail', label: 'Voicemail with urgency flag' },
              ]}
            />
          </SettingsFieldRow>
        </div>
      </section>
    </>
  );
}
